import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, Card, GBtn, Field, inputCls } from "./ui";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { mediaUrl } from "@/lib/api";
import { Plus, Trash2, Pencil, Save } from "lucide-react";

export default function CrudManager({ name, title, subtitle, fields, empty, renderSummary }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/admin/${name}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  const startNew = () => { setEditing({ ...empty }); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it }); setOpen(true); };

  const save = async () => {
    const body = { ...editing };
    fields.forEach((f) => { if (f.type === "number") body[f.name] = parseFloat(body[f.name]) || 0; });
    try {
      if (body.id) await api.put(`/admin/${name}/${body.id}`, body);
      else await api.post(`/admin/${name}`, body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/${name}/${id}`); toast.success("Deleted"); load(); };

  const uploadImage = async (file, fieldName) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd); setEditing((s) => ({ ...s, [fieldName]: data.url })); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <>
      <PageTitle title={title} subtitle={subtitle} right={<GBtn onClick={startNew} data-testid={`${name}-add`}><Plus size={16} /> Add new</GBtn>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <Card key={it.id} className="flex flex-col">
            {renderSummary(it)}
            <div className="mt-4 flex gap-2 pt-3 border-t border-white/10">
              <GBtn variant="ghost" onClick={() => startEdit(it)} data-testid={`${name}-edit-${it.id}`}><Pencil size={14} /> Edit</GBtn>
              <GBtn variant="danger" onClick={() => del(it.id)} data-testid={`${name}-delete-${it.id}`}><Trash2 size={14} /></GBtn>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-white/30 py-10">Nothing here yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {fields.map((f) => (
                <Field key={f.name} label={f.label}>
                  {f.type === "textarea" ? (
                    <textarea rows={3} value={editing[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls} />
                  ) : f.type === "switch" ? (
                    <div><Switch checked={!!editing[f.name]} onCheckedChange={(v) => set(f.name, v)} /></div>
                  ) : f.type === "image" ? (
                    <div className="space-y-2">
                      {editing[f.name] && <img src={mediaUrl(editing[f.name])} alt="" className="h-24 rounded-lg object-cover border border-white/10" />}
                      <input value={editing[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder="Image URL or upload below" className={inputCls} />
                      <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], f.name)} className="text-xs text-white/50" />
                    </div>
                  ) : (
                    <input type={f.type === "number" ? "number" : "text"} step={f.type === "number" ? "any" : undefined}
                      value={editing[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} className={inputCls} />
                  )}
                  {f.hint && <p className="text-[11px] text-white/35 mt-1">{f.hint}</p>}
                </Field>
              ))}
              <GBtn onClick={save} data-testid={`${name}-save`} className="w-full"><Save size={15} /> Save</GBtn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
